import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import JoinCard from './JoinCard';
import AISuggestions from './AISuggestions';
import { getMappingSectionKey, MAPPING_SECTION_LABELS, MAPPING_SECTION_ORDER } from '../utils/deriveJoinsFromMappings';

const MiddlePanel = ({
  mappings,
  setMappings,
  aiSuggestions,
  onJoinsChange,
  onAiSuggestionsChange,
  onRequestAI,
  disabled = false,
  mappingLoading = false,
  totalTargetColumns = 0,
  onAddMapping,
  onEditMapping,
  onReviewMapping,
  onDeleteMapping,
  onPreview,
  isPreviewReady,
  onSaveMappings,
  onRegenerateMappings,
  onOpenPreviewDialog,
}) => {
  const [joinDesignExpanded, setJoinDesignExpanded] = useState(true);
  const [aiSuggestionsExpanded, setAiSuggestionsExpanded] = useState(true);
  const [unionExpanded, setUnionExpanded] = useState(false);

  const { mappingsBySection, sectionOrder } = useMemo(() => {
    const map = { direct: [], compute: [], concat: [], join: [], aggregate: [], no_mapping: [], other: [] };
    (mappings || []).forEach((m, index) => {
      if (!m || typeof m !== 'object') return;
      if (m.target_column == null && m.mapping_type == null && !(m.source_columns?.length > 0)) return;
      const key = getMappingSectionKey(m.mapping_type);
      if (map[key]) map[key].push({ mapping: m, index });
    });
    const alwaysShow = ['direct', 'compute', 'join'];
    const withItems = MAPPING_SECTION_ORDER.filter((k) => (map[k]?.length || 0) > 0);
    const order = [...new Set([...alwaysShow, ...withItems])];
    return { mappingsBySection: map, sectionOrder: order };
  }, [mappings]);


  return (
    <Paper
      elevation={1}
      sx={{
        width: 520,
        height: 600,
        overflow: 'auto',
        p: 2,
        border: '1px solid #e5e7eb',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        userSelect: disabled ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Mappings Section */}
      <Accordion
        expanded={joinDesignExpanded}
        onChange={() => setJoinDesignExpanded(!joinDesignExpanded)}
        elevation={0}
        sx={{ mb: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, width: '100%' } }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              color: disabled ? 'text.disabled' : 'text.primary',
              opacity: disabled ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            Mappings
          </Typography>
          {(onPreview || onSaveMappings || onRegenerateMappings) && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              {onRegenerateMappings && (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  disabled={disabled}
                  onClick={onRegenerateMappings}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 0,
                    borderColor: '#00043c',
                    color: '#00043c',
                    '&:hover': { borderColor: '#000335', bgcolor: 'rgba(0, 4, 60, 0.04)' },
                  }}
                >
                  Regenerate mapping
                </Button>
              )}
              {onPreview && (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  disabled={!isPreviewReady || disabled}
                  onClick={onOpenPreviewDialog}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 0,
                    borderColor: '#00043c',
                    color: '#00043c',

                    '&:hover': {
                      borderColor: '#000335',
                      bgcolor: 'rgba(0, 4, 60, 0.04)',
                    },

                    /* ✅ Proper disabled styling */
                    '&.Mui-disabled': {
                      borderColor: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)',
                    },
                  }}
                >
                  <CircularProgress
                    size={14}
                    thickness={5}
                    sx={{
                      display: !isPreviewReady ? 'block' : 'none',
                      mr: !isPreviewReady ? 1 : 0,
                    }}
                  />

                  <span>{!isPreviewReady ? 'Preview Loading...' : 'Preview'}</span>
                </Button>
              )}
              {onSaveMappings && (
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  disabled={disabled}
                  onClick={onSaveMappings}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 0,
                    bgcolor: '#00043c',
                    color: '#fff',
                    '&:hover': { bgcolor: '#000335' },
                  }}
                >
                  Save mappings
                </Button>
              )}
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, display: 'block' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              maxHeight: 450,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 2,
              pb: 2,
              pt: 0,
            }}
          >
            {mappingLoading && (!mappings || mappings.length === 0) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  {totalTargetColumns > 0
                    ? `Preparing mappings for ${totalTargetColumns} target column${totalTargetColumns === 1 ? '' : 's'}...`
                    : 'Loading mapping suggestions...'}
                </Typography>
                <LinearProgress color="primary" sx={{ borderRadius: 1, height: 6 }} />
              </Box>
            ) : (
              <>
                {mappingLoading && mappings?.length > 0 && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#e3f2fd',
                      border: '1px solid',
                      borderColor: 'primary.light',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary.main"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                        Mapping target columns
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mappings.length}
                        {totalTargetColumns > 0 ? ` of ${totalTargetColumns}` : ''} ready
                      </Typography>
                    </Box>
                    {totalTargetColumns > 0 && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.round((mappings.length / totalTargetColumns) * 100))}
                        color="primary"
                        sx={{ borderRadius: 1, height: 6 }}
                      />
                    )}
                  </Box>
                )}
                {sectionOrder.map((sectionKey) => {
                  const sectionItems = mappingsBySection[sectionKey] || [];
                  const label = MAPPING_SECTION_LABELS[sectionKey] || sectionKey;
                  return (
                    <Box key={sectionKey} sx={{ pt: sectionKey === 'direct' ? 0 : 2 }}>
                      {sectionKey !== 'direct' && <Divider sx={{ mb: 1.5 }} />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            fontSize: '0.7rem',
                            letterSpacing: '0.08em',
                          }}
                        >
                          {label}
                        </Typography>
                        {sectionKey !== 'no_mapping' && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={disabled}
                            onClick={() => onAddMapping && onAddMapping(sectionKey)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              py: 0.5,
                              px: 1.5,
                              minHeight: 0,
                              bgcolor: '#00043c',
                              color: '#fff',
                              borderRadius: 2,
                              boxShadow: '0 1px 4px rgba(0, 4, 60, 0.3)',
                              '&:hover': {
                                bgcolor: '#000335',
                                boxShadow: '0 2px 8px rgba(0, 4, 60, 0.4)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {sectionItems.map(({ mapping, index }) => (
                          <JoinCard
                            key={`${sectionKey}-${index}`}
                            mapping={mapping}
                            editIndex={index}
                            disabled={disabled}
                            sectionKey={sectionKey}
                            onOpenReview={onReviewMapping ? () => onReviewMapping(mapping, index) : undefined}
                            onEdit={onEditMapping ? () => onEditMapping(mapping, index) : undefined}
                            onDelete={onDeleteMapping ? () => onDeleteMapping(index) : undefined}
                            showEditDelete
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* AI Suggestions Section */}
      {/* <Accordion
        expanded={aiSuggestionsExpanded}
        onChange={() => setAiSuggestionsExpanded(!aiSuggestionsExpanded)}
        elevation={0}
        sx={{ mb: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            AI Suggestions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AISuggestions
            suggestions={aiSuggestions}
            onSuggestionsChange={onAiSuggestionsChange}
            onRequestAI={onRequestAI}
            disabled={disabled}
          />
        </AccordionDetails>
      </Accordion> */}

      {/* Union and Schema Drift Section */}
      {/* <Accordion
        expanded={unionExpanded}
        onChange={() => setUnionExpanded(!unionExpanded)}
        elevation={0}
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Union and Schema Drift
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary">
            Union and schema drift detection tools will appear here.
          </Typography>
        </AccordionDetails>
      </Accordion> */}
    </Paper>
  );
};

export default MiddlePanel;
